<template>
    <div class="ph-logins-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
        >
            <template #loading>
                <div class="ph-logins-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #item.record_id="elem">
                <div v-if="elem.item.id != null">{{ elem.item.id }}</div>
            </template>

            <template #item.created_at="elem">
                <div v-if="elem.item.created_at != null">{{ useDataFormatWithTime(elem.item.created_at) }}</div>
            </template>
        </v-data-table-server>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import router from '@/router';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import SalesRepApi from '@/api/v1/SalesRepApi';
import { toast } from 'vue3-toastify';
import { useDataFormatWithTime } from '@/hooks/DataHooks';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type { ISalesRepLoginsResponse } from '@/models/SalesRepModel';

const headers = ref<ITableHeader[]>([
    {
        title: t('salesRep.recordIdLogins'),
        align: 'start',
        sortable: false,
        key: 'record_id',
        width: '150px',
        minWidth: '150px',
    },
    {
        title: t('salesRep.dataLogins'),
        align: 'center',
        sortable: false,
        key: 'created_at',
        width: '',
        minWidth: '160px',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<ISalesRepLoginsResponse[]>([]);
const salesRepApi = new SalesRepApi();
const salesRepLoginsList = async() => {
    loading.value = true;
    try {
        const result = await salesRepApi.salesRepLoginList(
            router.currentRoute.value.params.id,
            options.value.page,
            options.value.itemsPerPage,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
        );
        if (!result.isSuccess) {
            toast.error(t('salesRep.toast.errorSalesRepLoginsList'));
            loading.value = false;
            return;
        }
        currentPage.value = result.payload.current_page;
        lastPage.value = result.payload.last_page;
        total.value = result.payload.total;
        items.value = result.payload.data;
    } catch (e) {
        toast.error(t('salesRep.toast.errorSalesRepLoginsList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    salesRepLoginsList();
};
</script>

<style scoped lang="scss">
.ph-logins-table {
    &__loading {
        opacity: 0.6;
    }
}
</style>
